import api from './api';
import {
  MasterUserAdmin,
  MasterUserAdminCreateRequest,
  MasterUserAdminPagedResult,
  MasterUserAdminSearchParams,
  MasterUserAdminUpdateRequest,
  MasterUserTenantAssignmentDetail,
  MasterUserTenantAssignmentInput,
} from '../types';

const base = '/api/admin/master-users';

function normalizeTenantAssignment(raw: Record<string, unknown>): MasterUserTenantAssignmentDetail {
  return {
    tenantId: Number(raw.tenantId ?? raw.tenant_id),
    tenantCode: String(raw.tenantCode ?? raw.tenant_code ?? '').trim(),
    tenantName: String(raw.tenantName ?? raw.tenant_name ?? '').trim(),
    role: String(raw.role ?? 'User').trim() || 'User',
    isDefault: raw.isDefault !== undefined ? Boolean(raw.isDefault) : Boolean(raw.is_default ?? false),
    createdAt: (raw.createdAt ?? raw.created_at) as string | undefined,
  };
}

function normalizeMasterUser(raw: Record<string, unknown>): MasterUserAdmin {
  const tenantsRaw = raw.tenants ?? raw.Tenants;
  const tenants = Array.isArray(tenantsRaw)
    ? tenantsRaw.map((row) => normalizeTenantAssignment(row as Record<string, unknown>))
    : undefined;

  return {
    id: Number(raw.id),
    username: String(raw.username ?? '').trim(),
    displayName: String(raw.displayName ?? raw.display_name ?? '').trim() || undefined,
    email: String(raw.email ?? '').trim() || undefined,
    isActive: raw.isActive !== undefined ? Boolean(raw.isActive) : Boolean(raw.is_active ?? true),
    createdAt: (raw.createdAt ?? raw.created_at) as string | undefined,
    lastLoginAt: (raw.lastLoginAt ?? raw.last_login_at) as string | undefined,
    tenants,
    defaultTenantId: raw.defaultTenantId != null || raw.default_tenant_id != null
      ? Number(raw.defaultTenantId ?? raw.default_tenant_id)
      : undefined,
    defaultTenantCode: String(raw.defaultTenantCode ?? raw.default_tenant_code ?? '').trim() || undefined,
    defaultTenantName: String(raw.defaultTenantName ?? raw.default_tenant_name ?? '').trim() || undefined,
  };
}

function mapTenantInput(tenants: MasterUserTenantAssignmentInput[]) {
  return tenants.map((tenant) => ({
    tenantId: tenant.tenantId,
    role: tenant.role.trim() || 'User',
    isDefault: tenant.isDefault,
  }));
}

const masterUserAdminService = {
  listPaged: async (params: MasterUserAdminSearchParams): Promise<MasterUserAdminPagedResult> => {
    const response = await api.get(base, {
      params: {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        searchTerm: params.searchTerm || undefined,
        tenantId: params.tenantId ?? undefined,
      },
    });
    const data = response.data;
    const pageNumber = params.pageNumber || 1;
    const pageSize = params.pageSize || 10;

    if (Array.isArray(data)) {
      const items = data.map((row) => normalizeMasterUser(row as Record<string, unknown>));
      return {
        items,
        totalCount: items.length,
        pageNumber,
        pageSize,
        totalPages: Math.ceil(items.length / pageSize) || 0,
      };
    }

    if (data && typeof data === 'object' && 'items' in data) {
      const paged = data as MasterUserAdminPagedResult;
      return {
        ...paged,
        items: (paged.items as unknown[]).map((row) =>
          normalizeMasterUser(row as Record<string, unknown>),
        ),
      };
    }

    return {
      items: [],
      totalCount: 0,
      pageNumber,
      pageSize,
      totalPages: 0,
    };
  },

  getById: async (id: number): Promise<MasterUserAdmin> => {
    const response = await api.get(`${base}/${id}`);
    return normalizeMasterUser(response.data as Record<string, unknown>);
  },

  create: async (body: MasterUserAdminCreateRequest): Promise<MasterUserAdmin> => {
    const response = await api.post(base, {
      username: body.username.trim(),
      displayName: body.displayName?.trim() || undefined,
      email: body.email?.trim() || undefined,
      password: body.password,
      tenants: body.tenants?.length ? mapTenantInput(body.tenants) : undefined,
    });
    return normalizeMasterUser(response.data as Record<string, unknown>);
  },

  update: async (id: number, body: MasterUserAdminUpdateRequest): Promise<MasterUserAdmin> => {
    const response = await api.put(`${base}/${id}`, {
      displayName: body.displayName?.trim() || undefined,
      email: body.email?.trim() || undefined,
      password: body.password || undefined,
    });
    return normalizeMasterUser(response.data as Record<string, unknown>);
  },

  setUserTenants: async (
    id: number,
    tenants: MasterUserTenantAssignmentInput[],
  ): Promise<MasterUserTenantAssignmentDetail[]> => {
    const response = await api.put(`${base}/${id}/tenants`, {
      tenants: mapTenantInput(tenants),
    });
    const data = response.data;
    if (!Array.isArray(data)) return [];
    return data.map((row) => normalizeTenantAssignment(row as Record<string, unknown>));
  },

  setActiveStatus: async (id: number, isActive: boolean): Promise<MasterUserAdmin> => {
    const response = await api.patch(`${base}/${id}/status`, { isActive });
    return normalizeMasterUser(response.data as Record<string, unknown>);
  },
};

export default masterUserAdminService;
